import type {
  ChatServerAttachment,
  ChatServerChannel,
  ChatServerMessage,
  ChatServerMessageSender,
  ChatServerParticipant,
  ChatServerReaction,
  ResolvedParticipant,
} from './types'

/**
 * Pure converters from chat_server JSON (REST or WS) into the normalized store
 * shapes. Tolerant to both transports: REST nests `sender`/`user`, WS adds
 * `sender_id`. See *_json.ex / channel_room.ex for the source shapes.
 */

type RawUser = {
  id?: string
  external_id?: string | null
  name?: string | null
  avatar_url?: string | null
} | null

type RawParticipant = {
  id: string
  channel_id?: string
  user_id?: string
  role?: string | null
  is_primary?: boolean
  archived_at?: string | null
  favorited_at?: string | null
  last_read_at?: string | null
  user?: RawUser
}

type RawChannel = {
  id: string
  type: ChatServerChannel['type']
  name?: string | null
  metadata?: Record<string, unknown> | null
  external_id?: string | null
  created_by_id?: string | null
  participants?: RawParticipant[] | null
  unread_count?: number | null
  inserted_at?: string | null
  updated_at?: string | null
}

type RawSender = {
  id?: string
  external_id?: string | null
  name?: string | null
  avatar_url?: string | null
} | null

type RawMessage = {
  id: string
  channel_id: string
  type?: ChatServerMessage['type']
  body?: string | null
  attributes?: Record<string, unknown> | null
  attachments?: ChatServerAttachment[] | null
  reactions?: ChatServerReaction[] | null
  sender_id?: string | null
  sender?: RawSender
  edited_at?: string | null
  outdated_at?: string | null
  cancelled_at?: string | null
  inserted_at?: string | null
}

export const convertParticipant = (raw: RawParticipant, channelId?: string): ChatServerParticipant => ({
  id: raw.id,
  channel_id: raw.channel_id ?? channelId ?? '',
  user_id: raw.user_id ?? raw.user?.id ?? '',
  external_id: raw.user?.external_id ?? null,
  role: raw.role ?? null,
  is_primary: raw.is_primary ?? false,
  archived_at: raw.archived_at ?? null,
  favorited_at: raw.favorited_at ?? null,
  last_read_at: raw.last_read_at ?? null,
  name: raw.user?.name ?? null,
  avatar_url: raw.user?.avatar_url ?? null,
})

export const convertChannel = (raw: RawChannel): { channel: ChatServerChannel; participants: ChatServerParticipant[] } => {
  const participants = (raw.participants ?? []).map(p => convertParticipant(p, raw.id))
  const channel: ChatServerChannel = {
    id: raw.id,
    type: raw.type,
    name: raw.name ?? null,
    metadata: raw.metadata ?? {},
    external_id: raw.external_id ?? null,
    created_by_id: raw.created_by_id ?? null,
    participantIds: participants.map(p => p.id),
    unread_count: raw.unread_count ?? 0,
    inserted_at: raw.inserted_at ?? null,
    updated_at: raw.updated_at ?? null,
  }
  return { channel, participants }
}

const convertSender = (raw: RawSender): ChatServerMessageSender | null => {
  if (!raw || !raw.id) return null
  return {
    id: raw.id,
    external_id: raw.external_id ?? null,
    name: raw.name ?? null,
    avatar_url: raw.avatar_url ?? null,
  }
}

/**
 * Single message converter tolerant to REST (nested `sender`, no top-level
 * `sender_id`) and WS (`sender_id` + `sender`) shapes.
 */
export const convertMessage = <A = Record<string, unknown>>(raw: RawMessage): ChatServerMessage<A> => {
  const sender = convertSender(raw.sender ?? null)
  return {
    id: raw.id,
    channel_id: raw.channel_id,
    type: raw.type ?? 'text',
    body: raw.body ?? null,
    attributes: (raw.attributes ?? {}) as A,
    // OMITTED (not emptied) when the payload carries none — the embedded `last_message`
    // and WS edits drop these keys, and both merge paths are shallow, so an empty value wipes.
    ...(raw.attachments ? { attachments: raw.attachments } : null),
    ...(raw.reactions ? { reactions: raw.reactions } : null),
    sender_id: raw.sender_id ?? raw.sender?.id ?? null,
    sender,
    edited_at: raw.edited_at ?? null,
    outdated_at: raw.outdated_at ?? null,
    cancelled_at: raw.cancelled_at ?? null,
    inserted_at: raw.inserted_at ?? null,
  }
}

/**
 * Resolve the "other" participant's external_id (the user's chat_server_uid) for
 * a direct channel — the routing key behind `/m/:external_id`. Returns the first
 * participant whose external_id differs from the current user's chat_server_uid.
 */
export const channelToParticipantToken = (
  channel: { participantIds: string[] },
  participants: Record<string, ChatServerParticipant>,
  currentChatUid: string | number | null
): string | null => {
  const withToken = channel.participantIds.flatMap(id => (participants[id]?.external_id ? [participants[id]] : []))
  return pickOtherParticipant(withToken, currentChatUid)?.external_id ?? null
}

/**
 * The other side of a channel: not me, and a support operator only when nobody else is there. An
 * operator who joined a direct chat from admin2 stays a participant, and payload order is no guide.
 */
export const pickOtherParticipant = <P extends Pick<ChatServerParticipant, 'external_id' | 'role'>>(
  participants: readonly P[],
  currentChatUid: string | number | null
): P | null => {
  const others = participants.filter(p => currentChatUid === null || p.external_id === null || String(p.external_id) !== String(currentChatUid))
  return others.find(p => p.role !== 'admin') ?? others[0] ?? null
}

/** A deleted user is marked either way: a `deleted:<id>` chat id, or `kind: 'deleted'` from the resolve endpoint. */
export const isDeletedParticipant = (externalId: string | null | undefined, resolved?: Pick<ResolvedParticipant, 'kind'> | null) =>
  resolved?.kind === 'deleted' || !!externalId?.startsWith('deleted:')
